import {
  useBlockProps,
  InspectorControls,
  MediaUpload,
  MediaUploadCheck,
  PanelColorSettings
} from '@wordpress/block-editor'
import {
  PanelBody,
  TextControl,
  Button,
  ToggleControl,
  SelectControl,
  TextareaControl,
  RangeControl
} from '@wordpress/components'
import { __ } from '@wordpress/i18n'

export default function Edit ({ attributes, setAttributes }) {
  const {
    imageId,
    imageUrl,
    imageAlt,
    title,
    description,
    titleColor,
    descriptionColor,
    titleSize,
    linkUrl,
    linkTarget,
    titleAlign,
    descriptionAlign,
    iconAlign,
    backgroundColor,
    padding,
    iconSize
  } = attributes

  const blockProps = useBlockProps({
    className: `coziehomes-icon-blurb coziehomes-icon-blurb__editor coziehomes-icon-blurb__align-${iconAlign}`,
    style: {
      backgroundColor: backgroundColor || '#ffffff',
      padding: `${padding}px`
    }
  })

  const onSelectImage = media => {
    setAttributes({
      imageId: media.id,
      imageUrl: media.url,
      imageAlt: media.alt || ''
    })
  }

  const onRemoveImage = () => {
    setAttributes({
      imageId: 0,
      imageUrl: '',
      imageAlt: ''
    })
  }

  return (
    <>
      <InspectorControls>
        <PanelBody title={__('Icon Blurb Settings', 'coziehomes')}>
          <TextControl
            label={__('Title', 'coziehomes')}
            value={title}
            onChange={value => setAttributes({ title: value })}
            placeholder={__('Enter icon blurb title', 'coziehomes')}
          />
          <TextareaControl
            label={__('Description', 'coziehomes')}
            value={description}
            onChange={value => setAttributes({ description: value })}
            placeholder={__('Enter icon blurb description', 'coziehomes')}
          />
          <SelectControl
            label={__('Title Size', 'coziehomes')}
            value={titleSize}
            options={[
              { label: __('Small', 'coziehomes'), value: 'small' },
              { label: __('Medium', 'coziehomes'), value: 'medium' },
              { label: __('Large', 'coziehomes'), value: 'large' }
            ]}
            onChange={value => setAttributes({ titleSize: value })}
          />
          <SelectControl
            label={__('Title Alignment', 'coziehomes')}
            value={titleAlign}
            options={[
              { label: __('Left', 'coziehomes'), value: 'left' },
              { label: __('Center', 'coziehomes'), value: 'center' },
              { label: __('Right', 'coziehomes'), value: 'right' }
            ]}
            onChange={value => setAttributes({ titleAlign: value })}
          />
          <PanelColorSettings
            title='Title Color'
            colorSettings={[
              {
                value: titleColor,
                onChange: color => setAttributes({ titleColor: color }),
                label: 'Title Color'
              }
            ]}
          />
          <SelectControl
            label={__('Description Alignment', 'coziehomes')}
            value={descriptionAlign}
            options={[
              { label: __('Left', 'coziehomes'), value: 'left' },
              { label: __('Center', 'coziehomes'), value: 'center' },
              { label: __('Right', 'coziehomes'), value: 'right' }
            ]}
            onChange={value => setAttributes({ descriptionAlign: value })}
          />
          <PanelColorSettings
            title='Description Color'
            colorSettings={[
              {
                value: descriptionColor,
                onChange: color => setAttributes({ descriptionColor: color }),
                label: 'Description Color'
              }
            ]}
          />
          <PanelColorSettings
            title='Background Color'
            colorSettings={[
              {
                value: backgroundColor,
                onChange: color => setAttributes({ backgroundColor: color }),
                label: 'Background Color'
              }
            ]}
          />
          <RangeControl
            label={__('Icon Size', 'coziehomes')}
            value={iconSize}
            onChange={value => setAttributes({ iconSize: value })}
            min={0}
            max={96}
            step={4}
          />
          <SelectControl
            label={__('Icon Alignment', 'coziehomes')}
            value={iconAlign}
            options={[
              { label: __('Top', 'coziehomes'), value: 'top' },
              { label: __('Middle', 'coziehomes'), value: 'middle' },
              { label: __('Bottom', 'coziehomes'), value: 'bottom' }
            ]}
            onChange={value => setAttributes({ iconAlign: value })}
          />
          <RangeControl
            label={__('Padding (px)', 'coziehomes')}
            value={padding}
            onChange={value => setAttributes({ padding: value })}
            min={0}
            max={60}
            step={5}
          />
          <TextControl
            label={__('Link URL', 'coziehomes')}
            value={linkUrl}
            onChange={value => setAttributes({ linkUrl: value })}
            placeholder={__('https://example.com', 'coziehomes')}
            type='url'
          />
          <ToggleControl
            label={__('Open in new tab', 'coziehomes')}
            checked={linkTarget}
            onChange={value => setAttributes({ linkTarget: value })}
          />
        </PanelBody>
      </InspectorControls>
      <div {...blockProps}>
        <MediaUploadCheck>
          <MediaUpload
            onSelect={onSelectImage}
            allowedTypes={['image']}
            value={imageId}
            render={({ open }) => (
              <>
                {imageUrl ? (
                  <div className={`coziehomes-icon-blurb__img-wrapper`}>
                    <img
                      style={{ aspectRatio: 1, minWidth: `${iconSize}px` }}
                      height={iconSize}
                      width={iconSize}
                      src={imageUrl}
                      alt={imageAlt}
                      className='coziehomes-icon-blurb__img'
                      onClick={open}
                      role='presentation'
                    />
                    <Button
                      onClick={onRemoveImage}
                      isDestructive
                      size='small'
                      variant='secondary'
                      className='coziehomes-icon-blurb__remove-btn'
                    >
                      {__('Remove', 'coziehomes')}
                    </Button>
                  </div>
                ) : (
                  <div
                    className='coziehomes-icon-blurb__placeholder'
                    onClick={open}
                    role='button'
                    tabIndex={0}
                    onKeyDown={e => e.key === 'Enter' && open()}
                  >
                    <Button
                      size='small'
                      variant='primary'
                      className='coziehomes-icon-blurb__upload-btn'
                    >
                      {__('Select Image', 'coziehomes')}
                    </Button>
                  </div>
                )}
              </>
            )}
          />
        </MediaUploadCheck>

        <div className='coziehomes-icon-blurb__details'>
          {title ? (
            <h4
              className={`coziehomes-icon-blurb__title coziehomes-icon-blurb__title--align-${titleAlign} coziehomes-icon-blurb__title--size-${titleSize}`}
              style={{ color: titleColor }}
            >
              {title}
            </h4>
          ) : (
            <h4 className='coziehomes-icon-blurb__title coziehomes-icon-blurb__title--placeholder'>
              {__('Add a title…', 'coziehomes')}
            </h4>
          )}
          {description ? (
            <p
              className={`coziehomes-icon-blurb__description coziehomes-icon-blurb__description--align-${descriptionAlign}`}
              style={{ color: descriptionColor }}
              dangerouslySetInnerHTML={{
                __html: description.replace(/\n/g, '<br>')
              }}
            />
          ) : (
            <p className='coziehomes-icon-blurb__description coziehomes-icon-blurb__description--placeholder'>
              {__('Add a description…', 'coziehomes')}
            </p>
          )}
        </div>
      </div>
    </>
  )
}
